import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@mqd/volt-base";
import { Icon } from "@mqd/volt-core/src";
import { classes, useVoltState } from "@mqd/volt-toolbox/src";
import { LeftNavContent } from "./left-nav-content.js";
import { LeftNavLink } from "./left-nav-link.js";
import { LeftNavMenu } from "./left-nav-menu.js";
import css from "./left-nav.module.css";
import { TNavLinks } from "./types.js";
const LeftNavRoot = React.forwardRef(
  ({
    activePath = typeof window !== "undefined" ? window.location.pathname : null,
    className,
    header,
    links,
    persist,
    pinned,
    testId
  }, ref) => {
    const [leftNav, setLeftNav] = useVoltState((state) => [
      state.leftNav,
      state.setLeftNav
    ]);
    const dom = useVoltState((state) => state.dom);
    const [style, setStyle] = useState(null);
    const [tooltipLabel, setTooltipLabel] = useState(null);
    function handleTogglePinned() {
      setLeftNav({ pinned: !leftNav?.pinned });
    }
    const handleLeftNavHeight = useCallback(() => {
      const scrollbarHeight = window.innerHeight - document.documentElement.clientHeight;
      const topBarHeight = dom.topBar.current.getBoundingClientRect().height;
      setStyle({
        top: `${topBarHeight}px`,
        height: `calc(100vh - ${topBarHeight}px - ${scrollbarHeight}px)`
      });
    }, [dom.topBar, setStyle]);
    useEffect(() => {
      if (!dom.content.current)
        return;
      const contentResizeObserver = new ResizeObserver(handleLeftNavHeight);
      contentResizeObserver.observe(dom.content.current);
    }, [dom.content]);
    useEffect(() => {
      if (!setLeftNav)
        return;
      setLeftNav({
        show: Boolean(links),
        showHeader: Boolean(header),
        pinned
      });
    }, [links, pinned, setLeftNav]);
    useEffect(() => {
      if (!setLeftNav)
        return;
      setLeftNav({
        showHeader: Boolean(header)
      });
    }, [header, setLeftNav]);
    useEffect(() => {
      setTooltipLabel(leftNav?.pinned ? "Collapse" : "Expand");
    }, [leftNav?.pinned]);
    if (!leftNav?.show)
      return null;
    return /* @__PURE__ */ React.createElement(
      "aside",
      {
        id: "navigation",
        className: classes(
          css.container,
          !leftNav?.pinned && css["--collapsed"],
          className
        ),
        style,
        ref,
        "data-testid": testId
      },
      /* @__PURE__ */ React.createElement(
        Tooltip,
        {
          className: css.tooltip,
          content: tooltipLabel,
          direction: "right",
          preventAnimation: true
        },
        /* @__PURE__ */ React.createElement(
          "button",
          {
            className: css.leftNavCollapseToggle,
            onClick: handleTogglePinned,
            "aria-label": `${tooltipLabel} left navigation`,
            "aria-pressed": !leftNav?.pinned
          },
          /* @__PURE__ */ React.createElement(Icon, { name: "collapse", className: css.leftNavCollapseToggleArrow })
        )
      ),
      /* @__PURE__ */ React.createElement(
        LeftNavContent,
        {
          activePath,
          header,
          links,
          persist
        }
      )
    );
  }
);
LeftNavRoot.propTypes = {
  activePath: PropTypes.string,
  className: PropTypes.string,
  header: PropTypes.any,
  links: TNavLinks,
  persist: PropTypes.bool,
  pinned: PropTypes.bool,
  testId: PropTypes.string
};
LeftNavRoot.defaultProps = {
  activePath: null,
  className: null,
  links: [],
  persist: false,
  pinned: null,
  testId: null
};
LeftNavRoot.displayName = "LeftNav";
export const LeftNav = Object.assign(LeftNavRoot, {
  LeftNavContent,
  LeftNavLink,
  LeftNavMenu
});
